/**
 * Quill renders every embed - a `ql-formula` equation, for example - as
 *
 *    <span class="ql-formula" data-value="y=2x+4">\uFEFF<span contenteditable="false">…KaTeX…</span>\uFEFF</span>
 *
 * The two U+FEFF characters are `Embed`'s guard text nodes. They are created by
 * `node_modules/quill/blots/embed.js` so the caret cannot slip into the embed,
 * and they only mean something inside a live Quill instance. `quill.root.innerHTML`
 * serialises them anyway, so they end up inside `question_sheet`.
 *
 * U+FEFF cannot be represented in `latin1`, and MySQL/MariaDB rewrites every
 * character it cannot represent with a literal "?" (0x3F). A row written while
 * the connection/column was `latin1` therefore stores
 *
 *    <span class="ql-formula" data-value="y=2x+4">?<span contenteditable="false">…KaTeX…</span>?</span>
 *
 * The editor never shows that "?" because Quill's Formula blot re-renders the
 * equation from `data-value` and discards the stale text nodes. The read-only
 * and preview views inject the stored HTML verbatim instead, which is why the
 * stray "?" appears next to every equation in the preview but not in the editor.
 *
 * `stripQuillFormulaGuards` removes the guards (and their flattened "?" form)
 * before markup reaches a read-only renderer. It is deliberately conservative:
 * a "?" is only treated as a guard when it sits exactly where Quill writes one -
 * immediately inside the embed wrapper - so an ordinary question mark in the
 * teacher's wording is never touched.
 */

// Invisible embed guard / padding characters. Dropping them can never change
// what a student reads.
const GUARD_CHAR_RE = /[\uFEFF\u200B]/g;

// "<span …>" + guard + "<span contenteditable="  -> the embed's left guard.
const LEFT_GUARD_RE = />[\uFEFF\u200B?]+(?=<span contenteditable=)/g;

// "</span></span>" + guard + "</span>"  -> the embed's right guard. The double
// "</span>" is the KaTeX output span plus the embed's `contenteditable` span,
// which makes the pattern specific enough never to touch a nested `<span>`
// followed by a genuine "?" in the teacher's wording.
const RIGHT_GUARD_RE = /(<\/span><\/span>)[\uFEFF\u200B?]+(?=<\/span>)/g;

/**
 * Removes Quill's embed guard characters from stored editor markup.
 *
 * Safe to call on any string (plain text, null, undefined) and idempotent.
 *
 * @param {string} html HTML produced by `quill.root.innerHTML`.
 * @returns {string} The same HTML without embed guard characters.
 */
export const stripQuillFormulaGuards = (html) => {
   if (typeof html !== 'string' || html.indexOf("<") === -1) return html;

   // Fast path: nothing to clean (guard still intact or already stripped).
   if (!/[\uFEFF\u200B]/.test(html) && html.indexOf("?") === -1) return html;

   return html
      .replace(LEFT_GUARD_RE, ">")
      .replace(RIGHT_GUARD_RE, "$1")
      .replace(GUARD_CHAR_RE, "");
};

export default stripQuillFormulaGuards;
